import * as React from 'react'
import { FC, useEffect, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { LightTooltip, SideDrawerForm } from '@sales-center/components'

import { useUpdateFormFields } from './hooks'
import { ILeadOppProductFormModel } from './typings'
import { Props as FormProps } from './AddServiceForm'

import {
  IUserPreferenceGeneralListItemType,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'
import { useSelector } from 'react-redux'
import { AddServiceFormFieldsContent } from '@user/components/Services/AddServiceForm/AddServiceFormFieldsContent'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'

interface Props extends Omit<FormProps, 'initialValues'> {
  editMode: boolean
}

export const AddServiceFormFields: FC<Props> = (props) => {
  const { editMode, onClose, isMobile } = props
  const userGeneralPreferences = useSelector(userGeneralPreferencesList)
  const defaultEntityAnnualServiceOccurrencesSubtractOne =
    userGeneralPreferences?.find(
      (row) =>
        row?.type ==
        IUserPreferenceGeneralListItemType.EntityAnnualServiceOccurrencesSubtractOne,
    )
  const createServiceSubtractOne =
    defaultEntityAnnualServiceOccurrencesSubtractOne?.value || 'no'

  const updateFormFields = useUpdateFormFields(props)
  const { taxLoading, taxStatus, servicesLoading } = updateFormFields

  const { submitForm, isSubmitting, dirty, values, setFieldValue } =
    useFormikContext<ILeadOppProductFormModel>()

  const { annualOccurrences, serviceLocationId } = values

  useEffect(() => {
    if (annualOccurrences > 0 && !editMode) {
      setFieldValue('subtractOne', createServiceSubtractOne == 'yes')
    }
  }, [annualOccurrences, createServiceSubtractOne, editMode, setFieldValue])

  const disableSave = !dirty || isSubmitting || taxLoading || servicesLoading

  const saveBtnDisableInfo = useMemo(() => {
    if (serviceLocationId && taxStatus === 'error') {
      return 'Unable to add service because the service location does not match any tax code lookups in PestPac and there is no company/branch default tax code.'
    }
  }, [serviceLocationId, taxStatus])

  return isMobile ? (
    <>
      <DialogContent>
        <AddServiceFormFieldsContent {...props} {...updateFormFields} />
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button
          variant="text"
          onClick={onClose}
          data-test-id="sideDrawerCloseBtn"
        >
          Cancel
        </Button>

        <LightTooltip title={saveBtnDisableInfo || ''}>
          <Typography component="span">
            <Button
              color="primary"
              variant="contained"
              onClick={submitForm}
              disabled={disableSave}
              data-test-id="addServiceDrawerBtn"
            >
              {editMode ? 'Update Service' : 'Add Service'}
            </Button>
          </Typography>
        </LightTooltip>
      </DialogActions>
    </>
  ) : (
    <SideDrawerForm
      onSave={submitForm}
      onCancel={onClose}
      disableSave={disableSave}
      primaryBtnLabel={editMode ? 'Update Service' : 'Add Service'}
      padding="0 20px"
      saveBtnDataTestId="addServiceDrawerBtn"
      saveBtnDisableInfo={saveBtnDisableInfo}
      actionButtonHeight={'100%'}
    >
      <AddServiceFormFieldsContent {...props} {...updateFormFields} />
    </SideDrawerForm>
  )
}
AddServiceFormFields.displayName = 'AddServiceFormFields'
